import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Zap } from "lucide-react";
import { Button, Input } from "@/components/ui";
import {
  SettingsField,
  SettingsFieldGroup,
} from "@/components/settings/SettingsSection";
import { ConnectionTestResult } from "./ConnectionTestResult";

type ByomRuntime = "ollama" | "vllm" | "lmstudio" | "localai" | "other";

interface TestResult {
  success: boolean;
  error?: string;
  latencyMs: number;
}

export interface ByomConfigFormState {
  runtime: ByomRuntime;
  endpointUrl: string;
  modelName: string;
  visionModelName: string;
  apiKey: string;
}

interface ByomConfigFormProps {
  initialConfig?: {
    byomRuntime?: ByomRuntime;
    byomEndpointUrl?: string;
    byomModelName?: string;
    byomVisionModelName?: string;
    byomApiKeyEncrypted?: string;
  };
  onTestResult: (result: TestResult | null) => void;
  onChange: (config: ByomConfigFormState) => void;
  onTestConnection: (config: ByomConfigFormState) => Promise<void>;
  testResult: TestResult | null;
  isTesting: boolean;
}

const RUNTIME_OPTIONS: { value: ByomRuntime; label: string }[] = [
  { value: "ollama", label: "Ollama" },
  { value: "vllm", label: "vLLM" },
  { value: "lmstudio", label: "LM Studio" },
  { value: "localai", label: "LocalAI" },
  { value: "other", label: "Other" },
];

const RUNTIME_PLACEHOLDERS: Record<
  ByomRuntime,
  { url: string; model: string; vision: string }
> = {
  ollama: {
    url: "http://localhost:11434/v1",
    model: "llama3",
    vision: "llava",
  },
  vllm: {
    url: "http://localhost:8000/v1",
    model: "meta-llama/Llama-3-8B",
    vision: "",
  },
  lmstudio: {
    url: "http://localhost:1234/v1",
    model: "loaded-model",
    vision: "",
  },
  localai: {
    url: "http://localhost:8080/v1",
    model: "gpt-4",
    vision: "",
  },
  other: {
    url: "http://your-server:port/v1",
    model: "model-name",
    vision: "",
  },
};

export function ByomConfigForm({
  initialConfig,
  onTestResult,
  onChange,
  onTestConnection,
  testResult,
  isTesting,
}: ByomConfigFormProps) {
  const [runtime, setRuntime] = useState<ByomRuntime>(
    initialConfig?.byomRuntime ?? "ollama",
  );
  const [endpointUrl, setEndpointUrl] = useState(
    initialConfig?.byomEndpointUrl ?? "",
  );
  const [modelName, setModelName] = useState(
    initialConfig?.byomModelName ?? "",
  );
  const [visionModelName, setVisionModelName] = useState(
    initialConfig?.byomVisionModelName ?? "",
  );
  const [apiKey, setApiKey] = useState(
    initialConfig?.byomApiKeyEncrypted ? "***" : "",
  );

  const placeholders = RUNTIME_PLACEHOLDERS[runtime];

  const updateField = useCallback(
    (
      field: keyof ByomConfigFormState,
      value: string,
      currentState: ByomConfigFormState,
    ) => {
      const updated = { ...currentState, [field]: value };
      onChange(updated);
      onTestResult(null);
      return updated;
    },
    [onChange, onTestResult],
  );

  const handleRuntimeChange = useCallback(
    (newRuntime: ByomRuntime) => {
      setRuntime(newRuntime);
      updateField("runtime", newRuntime, {
        runtime: newRuntime,
        endpointUrl,
        modelName,
        visionModelName,
        apiKey,
      });
    },
    [endpointUrl, modelName, visionModelName, apiKey, updateField],
  );

  const handleEndpointUrlChange = useCallback(
    (value: string) => {
      setEndpointUrl(value);
      updateField("endpointUrl", value, {
        runtime,
        endpointUrl: value,
        modelName,
        visionModelName,
        apiKey,
      });
    },
    [runtime, modelName, visionModelName, apiKey, updateField],
  );

  const handleModelNameChange = useCallback(
    (value: string) => {
      setModelName(value);
      updateField("modelName", value, {
        runtime,
        endpointUrl,
        modelName: value,
        visionModelName,
        apiKey,
      });
    },
    [runtime, endpointUrl, visionModelName, apiKey, updateField],
  );

  const handleVisionModelNameChange = useCallback(
    (value: string) => {
      setVisionModelName(value);
      updateField("visionModelName", value, {
        runtime,
        endpointUrl,
        modelName,
        visionModelName: value,
        apiKey,
      });
    },
    [runtime, endpointUrl, modelName, apiKey, updateField],
  );

  const handleApiKeyChange = useCallback(
    (value: string) => {
      setApiKey(value);
      updateField("apiKey", value, {
        runtime,
        endpointUrl,
        modelName,
        visionModelName,
        apiKey: value,
      });
    },
    [runtime, endpointUrl, modelName, visionModelName, updateField],
  );

  const handleTestConnection = useCallback(async () => {
    await onTestConnection({
      runtime,
      endpointUrl,
      modelName,
      visionModelName,
      apiKey,
    });
  }, [
    onTestConnection,
    runtime,
    endpointUrl,
    modelName,
    visionModelName,
    apiKey,
  ]);

  return (
    <div className="space-y-5">
      {/* Runtime Selection */}
      <SettingsField label="Runtime" description="Select your LLM runtime">
        <div className="flex flex-wrap gap-2">
          {RUNTIME_OPTIONS.map((opt) => (
            <motion.button
              key={opt.value}
              type="button"
              onClick={() => handleRuntimeChange(opt.value)}
              whileTap={{ scale: 0.97 }}
              className={`px-4 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-200 border cursor-pointer ${
                runtime === opt.value
                  ? "border-accent bg-accent/8 text-accent"
                  : "border-border bg-white text-secondary hover:border-border hover:bg-card-hover hover:text-primary"
              }`}
            >
              {opt.label}
            </motion.button>
          ))}
        </div>
      </SettingsField>

      <SettingsFieldGroup columns={1}>
        <SettingsField label="Endpoint URL" description="Your LLM API endpoint">
          <Input
            value={endpointUrl}
            onChange={(e) => handleEndpointUrlChange(e.target.value)}
            placeholder={placeholders.url}
          />
        </SettingsField>
      </SettingsFieldGroup>

      <SettingsFieldGroup columns={2}>
        <SettingsField
          label="Model Name"
          description="Primary model for text generation"
        >
          <Input
            value={modelName}
            onChange={(e) => handleModelNameChange(e.target.value)}
            placeholder={placeholders.model}
          />
        </SettingsField>

        <SettingsField
          label="Vision Model Name"
          description="For file/image processing. Leave empty if primary model supports vision."
        >
          <Input
            value={visionModelName}
            onChange={(e) => handleVisionModelNameChange(e.target.value)}
            placeholder={placeholders.vision || "Optional"}
          />
        </SettingsField>
      </SettingsFieldGroup>

      <SettingsFieldGroup columns={1}>
        <SettingsField
          label="API Key"
          description="Leave as *** to keep current key"
        >
          <Input
            type="password"
            value={apiKey}
            onChange={(e) => handleApiKeyChange(e.target.value)}
            placeholder="Optional"
          />
        </SettingsField>
      </SettingsFieldGroup>

      {/* Test Connection */}
      <div className="space-y-3">
        <Button
          variant="secondary"
          icon={Zap}
          onClick={handleTestConnection}
          loading={isTesting}
          disabled={!endpointUrl || !modelName}
        >
          Test Connection
        </Button>

        <ConnectionTestResult result={testResult} isTesting={isTesting} />
      </div>
    </div>
  );
}
